import React from 'react';
import {
  TRUCK_POSITIONS,
  occupiedPositionsInRange,
  sortByLoadingPosition,
} from '../../utils/deliveryOccupancy';

/**
 * The vehicle's capacity, and what is currently loaded on it, for one
 * delivery run.
 *
 * The run IS the vehicle - there is no separate truck to look up. A run's
 * `number_of_positions` says what it is: 7 is a truck on its own, 13 is a
 * truck and a trailer. Positions 1-7 are the truck bed, 8-13 the trailer.
 *
 * The occupancy maths comes from `utils/deliveryOccupancy`, the same helper
 * the Planning page's truck graphic and sidebar use, so the Diary and the
 * Planning page can never disagree about how full a vehicle is. The server
 * enforces the same rules in `DeliveryOccupancyService`; the API also
 * returns `filled_positions` on the run, which is the authority.
 */

const DiaryRunLoad = ({ run }) => {
  const total = Number(run.total_positions || run.number_of_positions || 13);
  const deliveries = run.deliveries || [];

  const occupied = occupiedPositionsInRange(deliveries, total);
  const filled = occupied.size;
  const free = total - filled;

  const truckSlots = Math.min(TRUCK_POSITIONS, total);
  const trailerSlots = Math.max(0, total - TRUCK_POSITIONS);
  const truckFilled = [...occupied].filter((position) => position <= TRUCK_POSITIONS).length;
  const trailerFilled = filled - truckFilled;

  // The run sheet prints the stops by loading position, highest first, and
  // so does this - the Diary and the sheet a driver holds should read the
  // same way.
  //
  // Every stop is listed. A run can hold a dozen orders, and the planner
  // needs to see all of them, not a preview.
  const stops = sortByLoadingPosition(deliveries);

  const capacityText = [
    `${filled} of ${total} positions filled`,
    `${free} free`,
    trailerSlots > 0
      ? `(${truckSlots - truckFilled} truck, ${trailerSlots - trailerFilled} trailer)`
      : `(${free} truck)`,
  ].join(' · ');

  const ariaLabel =
    `${filled} of ${total} loading positions filled, ${free} free` +
    (trailerSlots > 0
      ? `. Truck ${truckFilled} of ${truckSlots}, trailer ${trailerFilled} of ${trailerSlots}.`
      : '.') +
    (stops.length ? ` ${stops.length} stops allocated.` : ' Nothing allocated yet.');

  const renderSlot = (position) => {
    // The first trailer slot carries the gap that separates the truck from
    // the trailer. Every slot is the same width, so a slot always means the
    // same amount of space - a truck and a trailer are separate vehicles
    // and the gap is the boundary between them.
    const isTrailerStart = trailerSlots > 0 && position === TRUCK_POSITIONS + 1;

    return (
      <span
        key={position}
        className={
          'diary-load-slot' +
          (occupied.has(position) ? ' diary-load-slot-filled' : '') +
          (isTrailerStart ? ' diary-load-slot-trailer-start' : '')
        }
      />
    );
  };

  return (
    <div className="diary-load">
      <div className="diary-load-bar" role="img" aria-label={ariaLabel}>
        {Array.from({ length: total }, (_, i) => renderSlot(i + 1))}
      </div>

      <span className="diary-load-summary">{capacityText}</span>

      {stops.length > 0 && (
        <ul className="diary-load-stops">
          {stops.map((delivery) => {
            const reference = (delivery.order || {}).order_external_reference;

            return (
              <li className="diary-load-stop" key={delivery.id}>
                {reference && <span className="diary-load-ref">{reference}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DiaryRunLoad;
